import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { citations, SORT_BY_RELEVANCE_FIELD, substances, SORT_BY_SIMILARITY_FIELD, SORT_BY_SIMILARITY_KEY_WORD } from '@Constants';
import { generateResultsPath } from '@AppRoutes/utils';
import history from '@AppRoutes/history';

const isStateParamsReady = ({ sort, order, pageNumber }) => !!sort && !!order && !!pageNumber;

const checkDefaultStateParams = (params, { sortingProperties, historyType }) => {
  const { sort, order, pageNumber, context, description } = params;
  if (!sortingProperties) return; // No sorting properties available, nothing to check
  if (sort && order) return; // Sort and order are already set, no need to check

  let defaultSortingPropertyImmutable;

  // Try to find sort by relevance sorting property for documents context
  const sortByRelevanceDefaultEnabled = !Auth.isFeatureEnabled(FEATURES.enableSortByPublicationYearDefault);
  if (sortByRelevanceDefaultEnabled && context === citations)
    defaultSortingPropertyImmutable = sortingProperties.find((prop) => prop.get('value') === SORT_BY_RELEVANCE_FIELD);

  // Try to find similarity sorting property for substances context
  const similarityOption = sortingProperties.find((prop) => prop.get('value') === SORT_BY_SIMILARITY_FIELD);
  if (
    context === substances &&
    similarityOption &&
    ((description && description.includes(SORT_BY_SIMILARITY_KEY_WORD)) ||
      (historyType && historyType.includes(SORT_BY_SIMILARITY_KEY_WORD)))
  ) {
    defaultSortingPropertyImmutable = similarityOption;
  }

  // If none of the previous cases found, find default sorting property
  if (!defaultSortingPropertyImmutable) {
    defaultSortingPropertyImmutable = sortingProperties.find((prop) => prop.get('default'));
  }

  // Extract sort and order values from the default sorting property
  const { value: defaultSort, order: defaultOrder } = defaultSortingPropertyImmutable ? defaultSortingPropertyImmutable.toJS() : {};

  const combinedParam = {
    ...params,
    ...history.location.state,
    sort: defaultSort,
    order: defaultOrder,
    pageNumber: pageNumber || 1,
  };

  const pathName = generateResultsPath(combinedParam);

  // Replace history with the new extended params
  history.replace({
    pathname: pathName,
    state: combinedParam,
  });
};

const checkResultsStateParams = () => (Component) => {
  function CheckResultsStateParamsHOC(props) {
    const { params, sortingProperties, activeBreadcrumb = {} } = props;
    const { historyType } = activeBreadcrumb;
    const stateParamsReady = isStateParamsReady(params);

    useEffect(() => {
      checkDefaultStateParams(params, { sortingProperties, historyType });
    }, [params, sortingProperties, historyType]);

    return <Component {...props} stateParamsReady={stateParamsReady} />;
  }

  CheckResultsStateParamsHOC.propTypes = {
    params: PropTypes.object.isRequired,
    sortingProperties: PropTypes.instanceOf(List),
    activeBreadcrumb: PropTypes.object,
  };

  return CheckResultsStateParamsHOC;
};

export default checkResultsStateParams;
